import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  Clock,
  FileText,
  Mail,
  MessageSquareText,
  Pencil,
  Phone,
  Plus,
  Trash2,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CollapsibleCard } from './_shared/CollapsibleCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useNoteMutations } from '@/features/patient-record/hooks/useNoteMutations';
import { patientQueries } from '@/features/patient-record/queries';
import {
  preciseNoteTime,
  relativeNoteTime,
} from '@/features/patient-record/lib/notesTime';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import type { NoteType, NoteViewModel } from '@/features/patient-record/types';

import { ConfirmDeleteDialog } from './_shared/ConfirmDeleteDialog';
import { useFocusOnMount } from './_shared/useFocusOnMount';

const NOTE_TYPES: readonly NoteType[] = ['NOTE', 'EMAIL', 'CALL'];
type NoteFilter = 'ALL' | NoteType;
const FILTER_OPTIONS: readonly NoteFilter[] = ['ALL', 'NOTE', 'EMAIL', 'CALL'];

/** Per-type visual + icon palette. Mirrors the design's t-note/t-email/t-call. */
const TYPE_STYLES: Record<NoteType, { chip: string; tag: string; icon: LucideIcon }> = {
  NOTE: {
    chip: 'bg-slate-100 text-slate-700',
    tag: 'bg-slate-100 text-slate-700',
    icon: FileText,
  },
  EMAIL: {
    chip: 'bg-sky-50 text-sky-700',
    tag: 'bg-sky-50 text-sky-800',
    icon: Mail,
  },
  CALL: {
    chip: 'bg-emerald-50 text-emerald-700',
    tag: 'bg-emerald-50 text-emerald-800',
    icon: Phone,
  },
};

interface NotesSectionProps {
  readonly guid: string;
}

/**
 * Notes & Correspondence section. Adapted from the PTV2 OH design handoff:
 *
 *   - Card shell with title + count + "Add note" outline button. Add and
 *     edit open the composer in a modal (matching the other record sections).
 *   - Filter chips with mono counts: All / Notes / Emails / Calls.
 *   - Per row: type-tinted icon chip, subject + one-line body preview;
 *     collapsed shows the author + relative time on the right, expanded
 *     moves them into a full-width footer with precise timestamp and
 *     Edit / Delete buttons (or "Authored by …" when the viewer is not
 *     the original author). Edit and delete are gated on authorship
 *     only — no edit-window timer.
 */
export function NotesSection({ guid }: NotesSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ ...patientQueries.notes(guid), placeholderData: keepPreviousData });
  const { data: me } = useMe();
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<NoteFilter>('ALL');

  const notes = useMemo(() => query.data ?? [], [query.data]);

  const editingNote =
    editingId !== null ? (notes.find((n) => n.id === editingId) ?? null) : null;

  const counts = useMemo<Record<NoteFilter, number>>(
    () => ({
      ALL: notes.length,
      NOTE: notes.filter((n) => n.noteType === 'NOTE').length,
      EMAIL: notes.filter((n) => n.noteType === 'EMAIL').length,
      CALL: notes.filter((n) => n.noteType === 'CALL').length,
    }),
    [notes],
  );

  const visible = useMemo(
    () => (filter === 'ALL' ? notes : notes.filter((n) => n.noteType === filter)),
    [filter, notes],
  );

  const openNew = (): void => {
    setComposerOpen(true);
    setEditingId(null);
  };
  const closeComposer = (): void => {
    setComposerOpen(false);
    setEditingId(null);
  };

  const summaryRows = notes.map((n) => ({
    label: n.subject !== null && n.subject.length > 0 ? n.subject : n.noteType,
    value: n.body.length > 80 ? `${n.body.slice(0, 80)}…` : n.body,
    filled: true,
  }));

  const addButton = (
    <Button size="sm" variant="outline" onClick={openNew}>
      <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
      {t('patient.notes.addNote')}
    </Button>
  );

  return (
    <>
      <CollapsibleCard
        idPrefix="notes"
        anchorId="notes"
        title={t('patient.notes.heading')}
        count={counts.ALL}
        headerAction={addButton}
        summary={summaryRows}
        flush
      >
        {notes.length > 0 && (
          <div className="flex items-center gap-3 px-5 py-3 border-b border-border">
            <NoteFilterChips value={filter} onChange={setFilter} counts={counts} />
          </div>
        )}

        <NotesListBody
          query={query}
          visible={visible}
          filter={filter}
          guid={guid}
          currentUserId={me?.id ?? null}
          expandedId={expandedId}
          onToggle={(id) => {
            setExpandedId((prev) => (prev === id ? null : id));
          }}
          onEdit={(note) => {
            setEditingId(note.id);
            setComposerOpen(false);
            setExpandedId(null);
          }}
        />
      </CollapsibleCard>

      {composerOpen && <NoteComposer guid={guid} onClose={closeComposer} />}
      {editingNote !== null && (
        <NoteComposer
          guid={guid}
          existing={editingNote}
          onClose={() => {
            setEditingId(null);
          }}
        />
      )}
    </>
  );
}

interface NoteFilterChipsProps {
  readonly value: NoteFilter;
  readonly onChange: (next: NoteFilter) => void;
  readonly counts: Record<NoteFilter, number>;
}

function NoteFilterChips({ value, onChange, counts }: NoteFilterChipsProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t('patient.notes.filter.label')}
      className="flex flex-wrap items-center gap-2"
    >
      {FILTER_OPTIONS.map((option) => {
        const active = option === value;
        const label =
          option === 'ALL'
            ? t('patient.notes.filter.all')
            : t(`patient.notes.filter.${option}`);
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            onClick={() => {
              onChange(option);
            }}
            className={cn(
              'inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-xs font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-brand text-brand-foreground border-brand'
                : 'bg-card text-muted-foreground border-border hover:border-muted-foreground/40 hover:text-card-foreground',
            )}
          >
            {label}
            <span
              className={cn(
                'font-mono text-[11px] font-semibold tabular-nums',
                active ? 'text-brand-foreground/75' : 'text-muted-foreground/70',
              )}
            >
              {counts[option]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface NotesListBodyProps {
  readonly query: ReturnType<typeof useQuery<NoteViewModel[]>>;
  readonly visible: NoteViewModel[];
  readonly filter: NoteFilter;
  readonly guid: string;
  readonly currentUserId: string | null;
  readonly expandedId: string | null;
  readonly onToggle: (id: string) => void;
  readonly onEdit: (note: NoteViewModel) => void;
}

function NotesListBody({
  query,
  visible,
  filter,
  guid,
  currentUserId,
  expandedId,
  onToggle,
  onEdit,
}: NotesListBodyProps): ReactElement {
  const { t } = useTranslation();

  if (query.isPending) {
    return (
      <div className="px-5 py-10 text-sm text-muted-foreground" role="status">
        {t('patient.notes.loading')}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="px-5 py-10">
        <p className="text-sm font-medium text-card-foreground">
          {t('patient.notes.error.heading')}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {t('patient.notes.error.message')}
        </p>
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div className="px-5 py-14 flex flex-col items-center text-center gap-1.5">
        <span className="h-12 w-12 rounded-full bg-muted text-muted-foreground grid place-items-center mb-1.5">
          <MessageSquareText className="h-5 w-5" aria-hidden="true" />
        </span>
        <h3 className="text-sm font-semibold text-card-foreground">
          {t(`patient.notes.emptyByFilter.${filter}.title`)}
        </h3>
        <p className="text-xs text-muted-foreground max-w-[40ch]">
          {t(`patient.notes.emptyByFilter.${filter}.body`)}
        </p>
      </div>
    );
  }

  return (
    <ul className="py-1 divide-y divide-border/60">
      {visible.map((note) => (
        <li key={note.id}>
          <NoteRow
            guid={guid}
            note={note}
            currentUserId={currentUserId}
            expanded={expandedId === note.id}
            onToggle={() => {
              onToggle(note.id);
            }}
            onEdit={() => {
              onEdit(note);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

interface NoteComposerProps {
  readonly guid: string;
  /** When set, composer is in edit mode and pre-fills from the note. */
  readonly existing?: NoteViewModel;
  readonly onClose: () => void;
}

interface NoteDraft {
  readonly body: string;
  readonly subject: string;
  readonly noteType: NoteType;
}

const toDraft = (existing?: NoteViewModel): NoteDraft => ({
  body: existing?.body ?? '',
  subject: existing?.subject ?? '',
  noteType: existing?.noteType ?? 'NOTE',
});

/**
 * Create / edit a note in a modal (mirrors the other patient-record dialogs).
 * The draft commits only on the explicit Save button; the dirty guard raises
 * Save / Discard / Keep editing on a dirty dismiss.
 */
function NoteComposer({ guid, existing, onClose }: NoteComposerProps): ReactElement {
  const { t } = useTranslation();
  const me = useMe();
  const { add, update } = useNoteMutations(guid);
  const subjectRef = useFocusOnMount<HTMLInputElement>();

  const userId = me.data?.id ?? 'unknown-user';
  const viewerName = me.data?.fullName ?? '';
  const isEdit = existing !== undefined;
  const label = t('patient.notes.formLabel', { defaultValue: 'Note' });
  const pending = isEdit ? update.isPending : add.isPending;

  const toPayload = (d: NoteDraft) => ({
    noteType: d.noteType,
    subject: d.subject.trim().length > 0 ? d.subject.trim() : null,
    body: d.body.trim(),
    userId,
  });

  const commit = (d: NoteDraft): Promise<unknown> =>
    existing !== undefined
      ? update.mutateAsync({ id: existing.id, ...toPayload(d) })
      : add.mutateAsync(toPayload(d));

  const form = useEditableDraft<NoteDraft>({
    initial: toDraft(existing),
    label,
    autosave: false,
    canSave: (d) => d.body.trim().length > 0,
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  // The explicit Save button: commit AND close.
  const submitAndClose = (): void => {
    if (!form.canSave) return;
    void commit(draft).then(onClose).catch(() => undefined);
  };

  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: pending,
    label,
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent
          className="sm:max-w-2xl max-h-[calc(100vh-3rem)] p-0 gap-0 flex flex-col"
          {...guard.contentProps}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>
              {isEdit
                ? t('patient.notes.composer.editingTitle')
                : t('patient.notes.addNote')}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={form.containerProps.ref}
            className="flex flex-col min-h-0 flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              submitAndClose();
            }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
              <TypeSegmentedControl
                value={draft.noteType}
                onChange={(next) => {
                  form.setDraft((d) => ({ ...d, noteType: next }));
                }}
              />
              <Input
                ref={subjectRef}
                type="text"
                value={draft.subject}
                onChange={(event) => {
                  form.setDraft((d) => ({ ...d, subject: event.target.value }));
                }}
                placeholder={t('patient.notes.subjectPlaceholder')}
                className="text-sm font-medium"
              />
              <Textarea
                value={draft.body}
                onChange={(event) => {
                  form.setDraft((d) => ({ ...d, body: event.target.value }));
                }}
                rows={6}
                placeholder={t(`patient.notes.composer.placeholder${draft.noteType}`)}
                className="min-h-[140px]"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0 border-t border-border px-6 py-3">
              <span className="text-[11px] text-muted-foreground truncate">
                {t(`patient.notes.type.${draft.noteType}`)} ·{' '}
                {isEdit
                  ? t('patient.notes.composer.hintEdit')
                  : t('patient.notes.composer.hintNew', { name: viewerName })}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={guard.requestClose}
                  disabled={pending}
                >
                  {t('patient.notes.composer.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={!form.canSave || pending}
                  loading={pending}
                  loadingText={t('patient.dirtyFooter.saving')}
                >
                  {isEdit
                    ? t('patient.notes.composer.saveEdit')
                    : t('patient.notes.composer.saveNew')}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}

interface TypeSegmentedControlProps {
  readonly value: NoteType;
  readonly onChange: (next: NoteType) => void;
}

function TypeSegmentedControl({ value, onChange }: TypeSegmentedControlProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="radiogroup"
      aria-label={t('patient.notes.typeLabel')}
      className="inline-flex items-center gap-0.5 p-0.5 bg-muted border border-border rounded-md"
    >
      {NOTE_TYPES.map((type) => {
        const active = value === type;
        const Icon = TYPE_STYLES[type].icon;
        return (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              onChange(type);
            }}
            className={cn(
              'inline-flex items-center gap-1.5 h-7 px-3 rounded text-xs font-semibold transition-colors',
              active
                ? 'bg-card text-card-foreground shadow-sm'
                : 'text-muted-foreground hover:text-card-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {t(`patient.notes.type.${type}`)}
          </button>
        );
      })}
    </div>
  );
}

interface NoteRowProps {
  readonly guid: string;
  readonly note: NoteViewModel;
  readonly currentUserId: string | null;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly onEdit: () => void;
}

function NoteRow({
  guid,
  note,
  currentUserId,
  expanded,
  onToggle,
  onEdit,
}: NoteRowProps): ReactElement {
  const { t } = useTranslation();
  const { remove } = useNoteMutations(guid);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const style = TYPE_STYLES[note.noteType];
  const Icon = style.icon;
  const subject = note.subject ?? t(`patient.notes.type.${note.noteType}`);
  const isUntitled = note.subject === null;
  const author = note.authorName ?? t('patient.notes.unknownAuthor');
  // Ownership-only gating: a note can only be edited / deleted by its
  // original author. Time-since-creation is irrelevant — there's no
  // edit window.
  const editable =
    currentUserId !== null &&
    note.authorAccountId !== null &&
    currentUserId === note.authorAccountId;
  const edited =
    note.modifiedOn !== null &&
    note.createdAt !== null &&
    note.modifiedOn !== note.createdAt;
  const bodyPreview = note.body.replace(/\s+/g, ' ').trim();

  return (
    <div
      className={cn(
        'group relative transition-colors',
        expanded ? 'bg-brand-soft/40' : 'hover:bg-muted/40',
      )}
    >
      <div
        className={cn(
          'grid grid-cols-[32px_minmax(0,1fr)_auto] gap-3.5 px-5 py-3.5',
          expanded ? 'cursor-default' : 'cursor-pointer',
        )}
        onClick={() => {
          if (!expanded) onToggle();
        }}
      >
        <span
          aria-hidden="true"
          className={cn('h-8 w-8 rounded-md grid place-items-center', style.chip)}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm font-medium truncate',
              isUntitled ? 'text-muted-foreground' : 'text-card-foreground',
            )}
          >
            {subject}
          </p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5 mb-1.5 min-w-0">
            <span className="font-semibold text-foreground/85">
              {t(`patient.notes.type.${note.noteType}`)}
            </span>
            <span className="w-[3px] h-[3px] rounded-full bg-muted-foreground/50" aria-hidden="true" />
            <span className="font-medium text-foreground/85 truncate">{author}</span>
            <span className="w-[3px] h-[3px] rounded-full bg-muted-foreground/50" aria-hidden="true" />
            <span title={preciseNoteTime(note.createdAt)} className="whitespace-nowrap">
              {relativeNoteTime(note.createdAt)}
            </span>
            {edited && (
              <>
                <span className="w-[3px] h-[3px] rounded-full bg-muted-foreground/50" aria-hidden="true" />
                <span className="italic">{t('patient.notes.edited')}</span>
              </>
            )}
          </p>
          {expanded ? (
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
              {note.body}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {bodyPreview}
            </p>
          )}
        </div>
        <div className="flex items-start gap-1 whitespace-nowrap">
          <div className="flex items-center gap-1">
            {editable && (
              <div
                className={cn(
                  'flex items-center gap-0.5',
                  // Always visible when expanded; only on hover when collapsed
                  // so resting rows stay clean.
                  expanded ? '' : 'hidden group-hover:flex',
                )}
              >
                <button
                  type="button"
                  aria-label={t('patient.notes.editNote')}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                  className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <Pencil className="h-[15px] w-[15px]" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={t('patient.notes.deleteNote')}
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmOpen(true);
                  }}
                  className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                </button>
              </div>
            )}
            <button
              type="button"
              aria-label={expanded ? t('patient.notes.collapse') : t('patient.notes.expand')}
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
              className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
              />
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="flex items-center gap-2 px-5 pb-3.5 pt-3 mt-1 border-t border-brand-soft">
          <Clock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
          <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
            {preciseNoteTime(note.createdAt)}
          </span>
          {!editable && (
            <>
              <span
                className="w-[3px] h-[3px] rounded-full bg-muted-foreground/50"
                aria-hidden="true"
              />
              <span className="text-[11px] text-muted-foreground/80">
                {t('patient.notes.authored_by', { name: author })}
              </span>
            </>
          )}
        </div>
      )}

      {confirmOpen && (
        <ConfirmDeleteDialog
          title={t('patient.notes.confirmDelete.title')}
          message={t('patient.notes.confirmDelete.message')}
          confirmLabel={t('patient.notes.confirmDelete.confirm')}
          busy={remove.isPending}
          onCancel={() => {
            setConfirmOpen(false);
          }}
          onConfirm={() => {
            remove.mutate(note.id, {
              onSuccess: () => {
                setConfirmOpen(false);
              },
            });
          }}
        />
      )}
    </div>
  );
}

